import React from 'react';
import { Card, CardContent } from '../ui/card';

/**
 * EmptyState Component
 * Per PocketFiler UI Style Guide - uses SVG icons, no emojis
 */
export function EmptyState({ 
  icon,  // Can be a Lucide icon component OR an SVG path string (e.g., "folder.svg")
  svgIcon,  // Explicit SVG path from /assets/
  title = 'No data yet', 
  description = 'Get started by creating something new',
  action
}) {
  // Render icon based on type
  const renderIcon = () => {
    if (svgIcon) {
      return (
        <img 
          src={`/assets/${svgIcon}`} 
          alt="" 
          className="h-8 w-8 opacity-70"
          aria-hidden="true"
        />
      );
    }
    
    if (typeof icon === 'string') {
      return (
        <img 
          src={`/assets/${icon}`} 
          alt="" 
          className="h-8 w-8 opacity-70"
          aria-hidden="true"
        />
      );
    }
    
    if (icon) {
      const IconComponent = icon;
      return <IconComponent className="h-8 w-8 text-muted-foreground" />;
    }
    
    // Default icon
    return (
      <img 
        src="/assets/folder.svg" 
        alt="" 
        className="h-8 w-8 opacity-70"
        aria-hidden="true"
      />
    );
  };

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          {renderIcon()}
        </div>
        <h3 className="font-medium text-lg mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground text-center max-w-sm mb-4">
          {description}
        </p>
        {action}
      </CardContent>
    </Card>
  );
}

/**
 * AIContentBox Component
 * For displaying AI-generated content with proper styling
 * Per PocketFiler UI Style Guide Section 8
 */
export function AIContentBox({ 
  title = 'AI Summary',
  children,
  className = ''
}) {
  return (
    <div className={`p-4 rounded-lg bg-accent/5 border border-accent/20 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <img 
          src="/assets/bulb.svg" 
          alt="" 
          className="h-4 w-4"
          aria-hidden="true"
        />
        <span className="font-medium text-sm text-accent">{title}</span>
      </div>
      <div className="text-sm text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

/**
 * InfoCard Component
 * For displaying key information with icon
 */
export function InfoCard({
  icon,  // SVG filename
  title,
  value,
  subtitle,
  className = ''
}) {
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg bg-muted/50 ${className}`}>
      {icon && (
        <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
          <img 
            src={`/assets/${icon}`} 
            alt="" 
            className="h-5 w-5"
            aria-hidden="true"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {title && <p className="text-xs text-muted-foreground">{title}</p>}
        {value && <p className="font-semibold text-lg">{value}</p>}
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}
